/**
 * Blog Grid – component-based controller for the blog-grid section.
 * Mirrors the behavior of the original blog-grid IIFE 1:1, but
 * encapsulated as a custom element. Each <blog-grid> instance fetches
 * its data from the alternate template `templates/blog.ajax.liquid`
 * via `?view=ajax&page=N`, paginates across all pages, and renders
 * sortable / searchable / paginated cards.
 */

const SELECTOR_DATA = '.js-blogData';
const SELECTOR_ARTICLES = '.js-blogArticles';
const SELECTOR_LOAD = '.js-blogLoad';
const SELECTOR_SORT = '.js-blogSort';
const SELECTOR_SORT_CURRENT = '.js-blogSortCurrent';
const SELECTOR_SORT_OPTION = '.js-blogSortOption';
const SELECTOR_SEARCH = '.js-blogSearch';
const SELECTOR_OVERLAY = '.js-blogOverlay';

class BlogGrid extends HTMLElement {
  constructor() {
    super();

    this.state = {
      sort: 'published_at-descending',
      search: null,
      paginate: 6,
      blogs: [],
      defaultArticles: [],
      articles: [],
      pages: [],
      currentPage: 0,
      totalPages: 0,
      maxAjaxPages: 1,
      url: null,
    };

    this._onSortToggle = this._onSortToggle.bind(this);
    this._onSortChange = this._onSortChange.bind(this);
    this._onSearchChange = this._onSearchChange.bind(this);
    this._onLoadMore = this._onLoadMore.bind(this);
    this._onOverlayClick = this._onOverlayClick.bind(this);
  }

  connectedCallback() {
    this._hydrateFromData();
    this._bindEvents();
    this._init();
  }

  disconnectedCallback() {
    this._unbindEvents();
  }

  /* --------------------------------------------------------------------- */
  /* Hydration                                                             */
  /* --------------------------------------------------------------------- */

  _hydrateFromData() {
    const elData = this.querySelector(SELECTOR_DATA);
    if (!elData) return;

    try {
      const data = JSON.parse(elData.textContent);
      this.state = { ...this.state, ...data };
    } catch (err) {
      console.warn('[blog-grid] failed to parse data:', err);
    }

    this.state.url = new URL(window.location.href);
  }

  /* --------------------------------------------------------------------- */
  /* AJAX – ?view=ajax (templates/blog.ajax.liquid)                        */
  /* --------------------------------------------------------------------- */

  /**
   * Fetch one page of articles from the AJAX template.
   * Response shape: [{ blog_id, total_articles, pages, parts, articles }, ...]
   */
  async _getArticles(page = 1) {
    const articlesUrl = new URL(window.location.href);
    articlesUrl.searchParams.set('view', 'ajax');
    articlesUrl.searchParams.set('page', page);

    const response = await fetch(articlesUrl);
    const data = await response.json();
    return data;
  }

  /**
   * Fetch pages 2..totalPages in parallel.
   */
  async _getAllArticles(totalPages) {
    if (totalPages == null || totalPages == 1) return;
    const requests = [];
    for (let i = 2; i <= totalPages; i++) {
      requests.push(this._getArticles(i));
    }
    return Promise.all(requests);
  }

  /**
   * After the initial render, fetch the rest of the pages and concat
   * them onto defaultArticles so search/sort/load-more cover the full
   * article set.
   */
  async _fetchAllArticles() {
    const results = await this._getAllArticles(this.state.maxAjaxPages || 1);
    if (!results || !results.length) return;
    const articles = results.flat().map((result) => result.articles).flat();
    this.state.defaultArticles = this.state.defaultArticles.concat(articles);
  }

  /**
   * Initial fetch – mirrors the IIFE's init() flow:
   *   getArticles() → setGrid() → setInitialSort() → fetchAllArticles()
   */
  async _init() {
    const results = await this._getArticles();
    if (!Array.isArray(results) || !results.length) {
      console.warn('[blog-grid] empty ajax response');
      return;
    }

    const maxPages = Math.max(...results.map((r) => r.pages || 1));
    const articles = results.flat().map((r) => r.articles).flat();

    this.state.defaultArticles = articles;
    this.state.maxAjaxPages = maxPages;
    this.state.firstAjaxResult = results;
    this.state.currentPage = 0;

    this._setInitialSort();

    this._fetchAllArticles();
  }

  /* --------------------------------------------------------------------- */
  /* Rendering                                                             */
  /* --------------------------------------------------------------------- */

  _articlesTemplate() {
    const { articles, blogs } = this.state;
    return articles.map((article) => {
      const blog = blogs.find((b) => b.id == article.blog_id);
      if (!blog) return '';
      return `
        <a href="/blogs/${blog.handle}/${article.handle}" class="article__card">
          <div class="article__card-image">
            <img src="${article.image?.src ?? ''}" alt="${article.image?.alt ?? ''}" width="415" height="534" />
          </div>
          <div class="article__card-content">
            <p class="article__card-blog">${blog.title}</p>
            <h2 class="article__card-title">${article.title}</h2>
          </div>
        </a>
      `;
    }).join('');
  }

  /* --------------------------------------------------------------------- */
  /* Sort                                                                  */
  /* --------------------------------------------------------------------- */

  _setInitialSort() {
    const param = this.state.url.searchParams.getAll('sort_by');
    const value = param.length ? param : this.state.sort;
    let elInput = this.querySelector(`${SELECTOR_SORT_OPTION}[value="${value}"]`);
    if (!elInput && value !== this.state.sort) {
      elInput = this.querySelector(`${SELECTOR_SORT_OPTION}[value="${this.state.sort}"]`);
    }
    if (!elInput) return;
    this._changeSort(elInput);
  }

  _changeSort(elInput) {
    const elSort = elInput.closest(SELECTOR_SORT);
    const elSortOptions = elSort.querySelectorAll('li');
    const elSortCurrent = elSort.querySelector(SELECTOR_SORT_CURRENT);
    const elArticles = this.querySelector(SELECTOR_ARTICLES);

    this.state.currentPage = 0;
    this.state.sort = elInput.value;
    this.state.url.searchParams.set('sort_by', elInput.value);

    window.history.pushState({}, '', this.state.url);

    elSortCurrent.innerHTML = elInput.labels[0].innerHTML;
    elSortOptions.forEach((el) => {
      el.classList.remove('active');
      el == elInput.parentNode && el.classList.add('active');
    });

    this.state.articles = this._sortArticles(elInput.value);
    elArticles.innerHTML = this._articlesTemplate();

    setTimeout(() => {
      elSort.classList.remove('open');
    }, 300);
  }

  _sortArticles(param) {
    const articlesToSort = this._searchedArticles();
    const p = param.split('-')[0];
    const d = param.split('-')[1];

    const sortedArticles = articlesToSort.sort((a, b) =>
      d == 'descending' ? b[p].localeCompare(a[p]) : a[p].localeCompare(b[p])
    );
    return this._createPages(sortedArticles);
  }

  /* --------------------------------------------------------------------- */
  /* Pagination                                                            */
  /* --------------------------------------------------------------------- */

  _createPages(articles) {
    const articlesPerPage = this.state.paginate;
    this.state.pages = [];

    for (let i = 0; i < articles.length; i += articlesPerPage) {
      this.state.pages.push(articles.slice(i, i + Number(articlesPerPage)));
    }

    this.state.totalPages = this.state.pages.length - 1;
    this._showHidePaginate();
    return this.state.pages[this.state.currentPage];
  }

  _showHidePaginate() {
    const elLoad = this.querySelector(SELECTOR_LOAD);
    if (!elLoad) return;

    if (this.state.currentPage < this.state.totalPages) {
      elLoad.classList.remove('hidden');
    } else {
      elLoad.classList.add('hidden');
    }
  }

  _onLoadMore(ev) {
    if (this.state.currentPage < this.state.totalPages) {
      this.state.currentPage++;
      this.state.articles = [...this.state.articles, ...this.state.pages[this.state.currentPage]];
      const elArticles = this.querySelector(SELECTOR_ARTICLES);
      elArticles.innerHTML = this._articlesTemplate();

      if (this.state.currentPage == this.state.totalPages) {
        ev.currentTarget.classList.add('hidden');
      }
    } else {
      this.state.currentPage = this.state.totalPages;
    }
  }

  /* --------------------------------------------------------------------- */
  /* Search                                                                */
  /* --------------------------------------------------------------------- */

  _searchedArticles() {
    const defaultArticles = [...this.state.defaultArticles].filter((e) => e);
    const { search, blogs } = this.state;
    return !!search ? defaultArticles.filter((article) => {
      const articleBlog = blogs.find((blog) => blog.id == article.blog_id);

      return article.author && article.author.toLowerCase().includes(search.toLowerCase()) ||
        article.body_html && article.body_html.toLowerCase().includes(search.toLowerCase()) ||
        article.tags && article.tags.toLowerCase().includes(search.toLowerCase()) ||
        article.summary_html && article.summary_html.toLowerCase().includes(search.toLowerCase()) ||
        article.title && article.title.toLowerCase().includes(search.toLowerCase()) ||
        articleBlog && articleBlog.title && articleBlog.title.toLowerCase().includes(search.toLowerCase());
    }) : defaultArticles;
  }

  _onSearchChange(ev) {
    ev.preventDefault();
    const elInput = ev.currentTarget;
    const elArticles = this.querySelector(SELECTOR_ARTICLES);
    const { value } = elInput;

    this.state.currentPage = 0;
    this.state.search = value ? value : null;
    this.state.articles = this._sortArticles(this.state.sort);

    elArticles.innerHTML = this.state.articles && this.state.articles.length > 0
      ? this._articlesTemplate()
      : `<div class="blog__grid-articles--noresults"><p>Your search for "${this._escapeHtml(value)}" returned no results.</p><p>Please try searching again.</p></div>`;
  }

  _escapeHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /* --------------------------------------------------------------------- */
  /* Filter / sort dropdown UI                                             */
  /* --------------------------------------------------------------------- */

  _onSortToggle(ev) {
    ev.preventDefault();

    const elToggle = ev.currentTarget;
    const elSortFilters = this.querySelectorAll(SELECTOR_SORT);
    const elOverlay = this.querySelector(SELECTOR_OVERLAY);
    const parentSort = elToggle.closest(SELECTOR_SORT);

    let isActive = false;
    elSortFilters.forEach((el) => {
      el == parentSort ? el.classList.toggle('open') : el.classList.remove('open');
      el.classList.contains('open') && (isActive = true);
    });

    isActive ? elOverlay.classList.remove('hidden') : elOverlay.classList.add('hidden');
  }

  _onOverlayClick(ev) {
    ev.preventDefault();
    const elOverlay = ev.currentTarget;
    this.querySelectorAll(SELECTOR_SORT).forEach((el) => el.classList.remove('open'));
    elOverlay.classList.add('hidden');
  }

  _onSortChange(ev) {
    this._changeSort(ev.currentTarget);
  }

  /* --------------------------------------------------------------------- */
  /* Event wiring                                                          */
  /* --------------------------------------------------------------------- */

  _bindEvents() {
    this.querySelectorAll(`${SELECTOR_SORT} button`).forEach((el) => {
      el.addEventListener('click', this._onSortToggle);
    });

    this.querySelectorAll(SELECTOR_SORT_OPTION).forEach((el) => {
      el.addEventListener('change', this._onSortChange);
    });

    this.querySelectorAll(SELECTOR_SEARCH).forEach((el) => {
      el.addEventListener('change', this._onSearchChange);
    });

    this.querySelectorAll(SELECTOR_LOAD).forEach((el) => {
      el.addEventListener('click', this._onLoadMore);
    });

    this.querySelectorAll(SELECTOR_OVERLAY).forEach((el) => {
      el.addEventListener('click', this._onOverlayClick);
    });
  }

  _unbindEvents() {
    this.querySelectorAll(`${SELECTOR_SORT} button`).forEach((el) => {
      el.removeEventListener('click', this._onSortToggle);
    });

    this.querySelectorAll(SELECTOR_SORT_OPTION).forEach((el) => {
      el.removeEventListener('change', this._onSortChange);
    });

    this.querySelectorAll(SELECTOR_SEARCH).forEach((el) => {
      el.removeEventListener('change', this._onSearchChange);
    });

    this.querySelectorAll(SELECTOR_LOAD).forEach((el) => {
      el.removeEventListener('click', this._onLoadMore);
    });

    this.querySelectorAll(SELECTOR_OVERLAY).forEach((el) => {
      el.removeEventListener('click', this._onOverlayClick);
    });
  }
}

if (!customElements.get('blog-grid')) {
  customElements.define('blog-grid', BlogGrid);
}
